import { useTranslation } from "react-i18next";
import { PlusIcon, TrashIcon, StarIcon } from "@heroicons/react/24/outline";
import { StarIcon as StarSolid } from "@heroicons/react/24/solid";
import { ContactsSelector } from "../../../utils/moduleSelectors/contactSelector";
import { EmployeeSelector } from "../../../utils/moduleSelectors/employeeSelector";
import {
  nestedFieldName,
  nestedRowFieldError,
} from "./agreementNestedFormSchemas";

function emptyContact(party = "customer") {
  return {
    id: null,
    contact_id: null,
    user_id: null,
    party,
    role: "",
    is_primary: false,
    contact: null,
    user: null,
  };
}

function FieldError({ message }) {
  if (!message) return null;
  return <p className="mt-1 text-xs font-medium text-rose-600">{message}</p>;
}

export function ServiceAgreementContactsEditor({
  contacts = [],
  customerId,
  onChange,
  formik,
}) {
  const { t } = useTranslation();

  const update = (index, patch) => {
    const next = contacts.map((row, i) =>
      i === index ? { ...row, ...patch } : row
    );
    onChange(next);
  };

  const setPrimary = (index) => {
    const party = contacts[index]?.party || "customer";
    onChange(
      contacts.map((row, i) => ({
        ...row,
        is_primary: row.party === party ? i === index : row.is_primary,
      }))
    );
  };

  const remove = (index) => {
    const row = contacts[index];
    if (row?.id) {
      onChange(
        contacts.map((item, i) =>
          i === index ? { ...item, _destroy: true } : item
        )
      );
      return;
    }
    onChange(contacts.filter((_, i) => i !== index));
  };

  const visible = contacts
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => !row._destroy);

  return (
    <div className="space-y-3">
      {visible.length === 0 ? (
        <p className="text-sm text-slate-500">
          {t("service.agreements.contacts.empty")}
        </p>
      ) : null}

      {visible.map(({ row, index }) => (
        <div
          key={row.id || `new-${index}`}
          className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4"
        >
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="flex rounded-full bg-white p-1 text-xs font-semibold shadow-sm ring-1 ring-slate-200">
              {["customer", "supplier"].map((party) => (
                <button
                  key={party}
                  type="button"
                  onClick={() =>
                    update(index, {
                      party,
                      contact_id: party === "customer" ? row.contact_id : null,
                      user_id: party === "supplier" ? row.user_id : null,
                    })
                  }
                  className={`rounded-full px-3 py-1 ${
                    row.party === party
                      ? "bg-[#1463C7] text-white"
                      : "text-slate-600"
                  }`}
                >
                  {t(`service.agreements.contacts.party.${party}`)}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPrimary(index)}
                className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-white"
                title={t("service.agreements.contacts.primary")}
              >
                {row.is_primary ? (
                  <StarSolid className="h-4 w-4 text-amber-500" />
                ) : (
                  <StarIcon className="h-4 w-4" />
                )}
                {t("service.agreements.contacts.primary")}
              </button>
              <button
                type="button"
                onClick={() => remove(index)}
                className="rounded-full p-2 text-rose-600 hover:bg-rose-50"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-6 gap-4">
            <div className="col-span-6 sm:col-span-3">
              {row.party === "supplier" ? (
                <>
                  <EmployeeSelector
                    name={nestedFieldName("contacts", index, "user_id")}
                    label={t("service.agreements.contacts.employee")}
                    selected={row.user}
                    onChange={(event) =>
                      update(index, {
                        user_id: event.target.value,
                        contact_id: null,
                        user: event.target.object,
                      })
                    }
                  />
                  <FieldError
                    message={nestedRowFieldError(
                      formik,
                      "contacts",
                      index,
                      "user_id"
                    )}
                  />
                </>
              ) : (
                <>
                  <ContactsSelector
                    name={nestedFieldName("contacts", index, "contact_id")}
                    label={t("service.agreements.contacts.contact")}
                    module_param={customerId}
                    selected={row.contact}
                    onChange={(event) =>
                      update(index, {
                        contact_id: event.target.value,
                        user_id: null,
                        contact: event.target.data,
                      })
                    }
                  />
                  <FieldError
                    message={nestedRowFieldError(
                      formik,
                      "contacts",
                      index,
                      "contact_id"
                    )}
                  />
                </>
              )}
            </div>
            <div className="col-span-6 sm:col-span-3">
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                {t("service.agreements.contacts.role")}
              </label>
              <input
                type="text"
                name={nestedFieldName("contacts", index, "role")}
                value={row.role || ""}
                onChange={(event) => update(index, { role: event.target.value })}
                placeholder={t("service.agreements.contacts.role_placeholder")}
                className="w-full rounded-[14px] border-[1.5px] border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900"
              />
            </div>
          </div>
        </div>
      ))}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onChange([...contacts, emptyContact("customer")])}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-slate-300"
        >
          <PlusIcon className="h-4 w-4" />
          {t("service.agreements.contacts.add_customer")}
        </button>
        <button
          type="button"
          onClick={() => onChange([...contacts, emptyContact("supplier")])}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-slate-300"
        >
          <PlusIcon className="h-4 w-4" />
          {t("service.agreements.contacts.add_supplier")}
        </button>
      </div>
    </div>
  );
}
